import { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getFleetDataByFilters,
  getInsights,
  InsightsQuery,
  insightsKey,
  LiveQuery,
  liveQueryKey,
} from 'store/fleet-management/fleet-management-actions';
import { selectFleetLiveEntry } from 'store/fleet-management/fleet-management-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { emptyDataset } from 'models/fleet-management/FleetDataSet';
import useLastSettled from './useLastSettled';

export type FleetDatasetRequest = LiveQuery;

/**
 * One live answer, stored under its key: any page or section asking the same question reads the
 * same answer, and a question already loading or recently answered is not asked again (see the
 * thunks' `condition`). The previous answer is held while the next one loads so a section dims
 * instead of blanking. A null key fetches nothing, for a section that waits on a selection.
 *
 * The request arrives rebuilt on most renders, so it is compared by its key, and `fetch` is read
 * from a ref rather than being an effect dependency.
 */
const useLiveQuery = <T>(key: string | null, fetch: () => void) => {
  const entry = useAppSelector((state) => selectFleetLiveEntry(state, key));

  const latestFetch = useRef(fetch);
  latestFetch.current = fetch;

  useEffect(() => {
    if (key != null) latestFetch.current();
  }, [key]);

  const failed = entry?.rest.getStatus === HttpFetchStatus.Failed;
  const settled = useLastSettled(failed ? undefined : entry?.data);

  return useMemo(
    () => ({
      data: failed ? null : (settled as T | null),
      isLoading: entry?.rest.getStatus === HttpFetchStatus.Loading,
      hasError: entry?.rest.getStatus === HttpFetchStatus.Failed,
    }),
    [entry?.rest.getStatus, failed, settled],
  );
};

/** The rows of one posted Insights dataset. */
const useFleetDataset = <T>(request: FleetDatasetRequest | null) => {
  const dispatch = useAppDispatch();
  const key = request == null ? null : liveQueryKey(request);

  return useLiveQuery<T[]>(key, () => {
    if (key == null || request == null) return;
    const { model, start, end, filters, fields } = request;
    dispatch(
      getFleetDataByFilters({
        datasetIds: key,
        payload: {
          ...emptyDataset,
          model,
          start,
          end,
          filters,
          fields: fields ?? [],
          // The key carries the filters and runs well past the 100 characters a dataset name
          // may hold, so the name only says what is asked for.
          name: `live ${model}`,
        },
      }),
    );
  });
};

/** The answer of one health and performance endpoint, e.g. `health-changes`. */
export const useFleetInsights = <T>(path: string, query: InsightsQuery | null) => {
  const dispatch = useAppDispatch();
  const key = query == null ? null : insightsKey(path, query);

  return useLiveQuery<T>(key, () => {
    if (key != null && query != null) dispatch(getInsights({ key, path, query }));
  });
};

export default useFleetDataset;
